import type { BadgeTone } from '@/components/ui';
import type {
  RxDocumentRevokedReason,
  RxExcludedReason,
  RxPatientAllergy,
  RxRequestItem,
  RxRequestKind,
  RxRequestStatus,
  RxRouteReason,
  RxWithdrawnReason,
} from '@/features/prescription-requests/types';
import { CLINIC_TZ } from '@/lib/clinic-time';

export const KIND_LABEL: Record<RxRequestKind, string> = {
  refill: 'Refill',
  outside_pharmacy: 'Outside pharmacy',
};

export const STATUS_LABEL: Record<RxRequestStatus, string> = {
  pending: 'Waiting',
  approved: 'Approved',
  refused: 'Refused',
  withdrawn: 'Withdrawn',
};

export const STATUS_TONE: Record<RxRequestStatus, BadgeTone> = {
  pending: 'warning',
  approved: 'success',
  refused: 'danger',
  withdrawn: 'neutral',
};

export const ROUTE_REASON_LABEL: Record<RxRouteReason, string> = {
  prescriber: 'the prescriber',
  branch_inbox: 'any doctor of the branch',
  claimed: 'claimed by the deciding doctor',
  reassigned: 'routed by the clinic manager',
};

export const EXCLUDED_REASON_LABEL: Record<RxExcludedReason, string> = {
  controlled: 'Controlled medicine: stays at the clinic counter',
  psychotropic: 'Psychotropic: stays at the clinic counter',
  poison_group_a: 'Poisons Act group A: stays at the clinic counter',
  not_in_catalogue:
    'Not in the clinic’s medicine list: stays at the clinic counter',
  already_dispensed:
    'Already handed over by the clinic: not written again for a pharmacy',
};

export const WITHDRAWN_REASON_LABEL: Record<RxWithdrawnReason, string> = {
  patient: 'Withdrawn by the patient in the app',
  account_deleted: 'The patient’s app account was deleted',
  prescription_cancelled: 'The prescription was cancelled',
};

export const REVOKED_REASON_LABEL: Record<RxDocumentRevokedReason, string> = {
  supplied_at_clinic: 'the clinic supplied the medicines itself',
  prescription_cancelled: 'the prescription was cancelled',
  other: 'revoked by the clinic',
};

/**
 * "23 Oct 2026, 5:14 pm" in clinic time (Asia/Kuala_Lumpur), whatever the
 * computer's own time zone (RL-1895): the API sends UTC instants.
 */
export const when = (iso: string) =>
  new Date(iso).toLocaleString('en-MY', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: CLINIC_TZ,
  });

/** "1 tablet · Twice a day · 30 days · 60 tablet" (missing parts skipped). */
export function itemDirections(i: RxRequestItem): string {
  return [
    i.dose,
    i.frequency,
    i.durationDays != null ? `${i.durationDays} days` : null,
    i.quantity != null ? `${i.quantity}${i.unit ? ` ${i.unit}` : ''}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

/**
 * The item's allergy alerts, each saying whether it is about a
 * patient-reported (not yet confirmed) allergy. RL-1256: from the API's
 * `allergyAlertDetails[].reported`; only an API without it (RL-1181) falls
 * back to reading the text.
 */
export function itemAlerts(
  item: RxRequestItem,
  allergies: RxPatientAllergy[] | null,
): { text: string; reported: boolean }[] {
  if (item.allergyAlertDetails)
    return item.allergyAlertDetails.map((d) => ({
      text: d.text,
      reported: d.reported,
    }));
  return item.allergyAlerts.map((text) => ({
    text,
    reported: isReportedAlert(text, allergies),
  }));
}

/**
 * Fallback for an API without `allergyAlertDetails`: the alert text says
 * "Patient-reported in the app, not confirmed yet"; a reported allergy whose
 * substance the alert names counts too.
 */
export function isReportedAlert(
  alert: string,
  allergies: RxPatientAllergy[] | null,
): boolean {
  const text = alert.toLowerCase();
  if (text.includes('patient-reported')) return true;
  return (allergies ?? []).some(
    (a) =>
      a.status === 'reported' &&
      text.includes(`allergy: ${a.substance.toLowerCase()}`),
  );
}

export const alertCount = (items: RxRequestItem[] | null) =>
  (items ?? []).reduce((n, i) => n + i.allergyAlerts.length, 0);
